import React, { useState, useEffect } from 'react';
import { InformePericial, InformeFormData, FaseInforme } from '../../types';
import { informeService, ubicacionService } from '../../services/api';
import { PROVINCIAS } from '../../constants';
import './EncargoForm.css';

interface EncargoFormProps {
    informe?: InformePericial;
    isNew?: boolean;
    onUpdate: (informe: InformePericial, targetFase?: FaseInforme) => void;
}

const EncargoForm: React.FC<EncargoFormProps> = ({ informe, isNew, onUpdate }) => {
    const [formData, setFormData] = useState<InformeFormData>({
        numero_expediente: '',
        fecha_apertura: new Date().toISOString().split('T')[0],
        fecha_encargo: new Date().toISOString().split('T')[0],
        fecha_siniestro: '',
        fecha_efecto: '',
        fecha_limite_entrega: '',
        numero_siniestro: '',
        numero_poliza: '',
        compania: '',
        ramo: '',
        tipo: '',
        observaciones: '',
        apellidos: '',
        nombre: '',
        tipo_via: '',
        direccion: '',
        poblacion: '',
        provincia: '',
        codigo_postal: '',
        telefono: '',
        contacto: '',
        preexistencias_continente: '',
        preexistencias_contenido: '',
        preexistencias_responsabilidad_civil: '',
        preexistencias_continente_calculado: '',
        preexistencias_contenido_calculado: '',
        preexistencias_responsabilidad_civil_calculada: ''
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [plazosCompania, setPlazosCompania] = useState<any[]>([]);

    useEffect(() => {
        informeService.obtenerPlazosCompania()
            .then(res => setPlazosCompania(res.data || []))
            .catch(err => console.error("Error al cargar plazos de compañía:", err));
    }, []);

    useEffect(() => {
        if (informe) {
            setFormData({
                numero_expediente: informe.numero_expediente || '',
                fecha_apertura: informe.fecha_apertura?.split('T')[0] || '',
                fecha_encargo: informe.fecha_encargo?.split('T')[0] || informe.fecha_apertura?.split('T')[0] || '',
                fecha_siniestro: informe.fecha_siniestro?.split('T')[0] || '',
                fecha_efecto: informe.fecha_efecto?.split('T')[0] || '',
                fecha_limite_entrega: informe.fecha_limite_entrega?.split('T')[0] || '',
                numero_siniestro: informe.numero_siniestro || '',
                numero_poliza: informe.numero_poliza || '',
                compania: informe.compania || '',
                ramo: informe.ramo || '',
                tipo: informe.tipo || '',
                observaciones: informe.observaciones || '',
                apellidos: informe.apellidos || '',
                nombre: informe.nombre || '',
                tipo_via: informe.tipo_via || '',
                direccion: informe.direccion || '',
                poblacion: informe.poblacion || '',
                provincia: informe.provincia || '',
                codigo_postal: informe.codigo_postal || '',
                telefono: informe.telefono || '',
                contacto: informe.contacto || '',
                preexistencias_continente: informe.preexistencias_continente || '',
                preexistencias_contenido: informe.preexistencias_contenido || '',
                preexistencias_responsabilidad_civil: informe.preexistencias_responsabilidad_civil || '',
                preexistencias_continente_calculado: informe.preexistencias_continente_calculado || '',
                preexistencias_contenido_calculado: informe.preexistencias_contenido_calculado || '',
                preexistencias_responsabilidad_civil_calculada: informe.preexistencias_responsabilidad_civil_calculada || ''
            });
        }
    }, [informe]);

    const prvHandleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        setFormData(prev => {
            const nextData = { ...prev, [name]: value };

            if (name === 'compania' || name === 'fecha_encargo' || name === 'fecha_apertura') {
                const compNombre = name === 'compania' ? value : prev.compania;
                const fechaBase = (name === 'fecha_encargo' ? value : prev.fecha_encargo) || (name === 'fecha_apertura' ? value : prev.fecha_apertura);

                if (compNombre && plazosCompania.length > 0) {
                    const match = plazosCompania.find(p => p.compania.toLowerCase() === compNombre.trim().toLowerCase());
                    if (match && match.dias_limite_entrega) {
                        const baseDate = fechaBase ? new Date(fechaBase) : new Date();
                        if (!isNaN(baseDate.getTime())) {
                            baseDate.setDate(baseDate.getDate() + match.dias_limite_entrega);
                            nextData.fecha_limite_entrega = baseDate.toISOString().split('T')[0];
                        }
                    } else {
                        // Si no se encuentra la aseguradora en la configuración de plazos, se deja vacío
                        nextData.fecha_limite_entrega = '';
                    }
                } else if (!compNombre) {
                    nextData.fecha_limite_entrega = '';
                }
            }

            return nextData;
        });
    };

    const prvHandlePoblacionBlur = async () => {
        if (!formData.poblacion || formData.poblacion.trim() === '') return;
        if (formData.provincia && formData.provincia.trim() !== '') return;

        try {
            const res = await ubicacionService.obtenerProvincia(formData.poblacion.trim());
            if (res.data && res.data.provincia) {
                const provEncontrada = res.data.provincia;
                const provMatch = PROVINCIAS.find(p => p.toLowerCase() === provEncontrada.toLowerCase()) || provEncontrada;
                setFormData(prev => ({
                    ...prev,
                    provincia: provMatch
                }));
            }
        } catch (err) {
            console.error('Error al autocompletar la provincia:', err);
        }
    };



    const prvHandleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (!formData.numero_expediente || !formData.numero_siniestro) {
                throw new Error('Por favor completa los campos obligatorios (*)');
            }

            let response;
            if (isNew) {
                response = await informeService.crearInforme(formData);
            } else if (informe) {
                response = await informeService.actualizarInforme(informe.id, formData);
            }

            if (response && response.data) {
                let updatedInforme = response.data;
                if (updatedInforme.fase === 'encargo') {
                    try {
                        const phaseRes = await informeService.cambiarFase(updatedInforme.id, 'cita');
                        updatedInforme = phaseRes.data;
                    } catch (e) {
                        console.error('Error al actualizar fase en backend:', e);
                    }
                }
                onUpdate(updatedInforme, 'cita');
            }
        } catch (err: any) {
            console.error('Error al guardar:', err);
            setError(err.response?.data?.error || err.message || 'Error al guardar el encargo');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={prvHandleSubmit} className="encargo-form">
            <div className="flex justify-between items-center mb-6 sticky top-0 bg-white z-10 pt-2">
                <h2 className="text-xl font-bold text-gray-800">
                    {isNew ? 'Nuevo Encargo' : 'Detalles del Encargo'}
                </h2>
                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                >
                    {loading ? 'Guardando...' : (isNew ? 'Crear Expediente' : 'Siguiente Fase')}
                </button>
            </div>

            {error && <div className="error-message mb-4">{error}</div>}

            <div className="form-grid">
                {/* Datos del Expediente */}
                <section className="form-section">
                    <h3 className="section-title">Datos del Expediente</h3>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Nº Expediente *</label>
                            <input
                                type="text"
                                name="numero_expediente"
                                value={formData.numero_expediente}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Fecha Apertura *</label>
                            <input
                                type="date"
                                name="fecha_apertura"
                                value={formData.fecha_apertura}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Fecha Encargo *</label>
                            <input
                                type="date"
                                name="fecha_encargo"
                                value={formData.fecha_encargo || ''}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Fecha Siniestro *</label>
                            <input
                                type="date"
                                name="fecha_siniestro"
                                value={formData.fecha_siniestro}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Fecha Efecto *</label>
                            <input
                                type="date"
                                name="fecha_efecto"
                                value={formData.fecha_efecto}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Fecha Fin Servicio (Límite)</label>
                            <input
                                type="date"
                                name="fecha_limite_entrega"
                                value={formData.fecha_limite_entrega || ''}
                                onChange={prvHandleChange}
                                className="input"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Nº Siniestro *</label>
                            <input
                                type="text"
                                name="numero_siniestro"
                                value={formData.numero_siniestro}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Nº Póliza *</label>
                            <input
                                type="text"
                                name="numero_poliza"
                                value={formData.numero_poliza}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Compañía *</label>
                            <input
                                type="text"
                                name="compania"
                                value={formData.compania}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Ramo *</label>
                            <input
                                type="text"
                                name="ramo"
                                value={formData.ramo}
                                onChange={prvHandleChange}
                                required
                                className="input"
                                placeholder="Ej: HOGAR"
                            />
                        </div>
                        <div className="form-group">
                            <label>Tipo *</label>
                            <input
                                type="text"
                                name="tipo"
                                value={formData.tipo}
                                onChange={prvHandleChange}
                                required
                                className="input"
                                placeholder="Ej: ROBO"
                            />
                        </div>
                    </div>
                </section>

                {/* Datos del Asegurado */}
                <section className="form-section">
                    <h3 className="section-title">Datos del Asegurado</h3>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Nombre *</label>
                            <input
                                type="text"
                                name="nombre"
                                value={formData.nombre}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Apellidos *</label>
                            <input
                                type="text"
                                name="apellidos"
                                value={formData.apellidos}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Tipo Vía</label>
                            <select
                                name="tipo_via"
                                value={formData.tipo_via}
                                onChange={prvHandleChange}
                                className="input"
                            >
                                <option value="">Seleccionar...</option>
                                <option value="Calle">Calle</option>
                                <option value="Avenida">Avenida</option>
                                <option value="Plaza">Plaza</option>
                                <option value="Travesía">Travesía</option>
                                <option value="Paseo">Paseo</option>
                                <option value="Camino">Camino</option>
                            </select>
                        </div>
                        <div className="form-group flex-2">
                            <label>Dirección *</label>
                            <input
                                type="text"
                                name="direccion"
                                value={formData.direccion}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Código Postal *</label>
                            <input
                                type="text"
                                name="codigo_postal"
                                value={formData.codigo_postal}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Población *</label>
                            <input
                                type="text"
                                name="poblacion"
                                value={formData.poblacion}
                                onChange={prvHandleChange}
                                onBlur={prvHandlePoblacionBlur}
                                required
                                className="input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Provincia *</label>
                            <select
                                name="provincia"
                                value={formData.provincia}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            >
                                <option value="">Seleccionar...</option>
                                {PROVINCIAS.map(p => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Teléfono *</label>
                            <input
                                type="tel"
                                name="telefono"
                                value={formData.telefono}
                                onChange={prvHandleChange}
                                required
                                className="input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Otro Contacto</label>
                            <input
                                type="text"
                                name="contacto"
                                value={formData.contacto}
                                onChange={prvHandleChange}
                                className="input"
                                placeholder="Persona de contacto"
                            />
                        </div>
                    </div>
                </section>

                {/* Preexistencias */}
                <section className="form-section">
                    <h3 className="section-title">Preexistencias y Capitales</h3>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Continente (Póliza)</label>
                            <input
                                type="text"
                                name="preexistencias_continente"
                                value={formData.preexistencias_continente || ''}
                                onChange={prvHandleChange}
                                className="input"
                                placeholder="Ej: 150.000 €"
                            />
                        </div>
                        <div className="form-group">
                            <label>Contenido (Póliza)</label>
                            <input
                                type="text"
                                name="preexistencias_contenido"
                                value={formData.preexistencias_contenido || ''}
                                onChange={prvHandleChange}
                                className="input"
                                placeholder="Ej: 30.000 €"
                            />
                        </div>
                        <div className="form-group">
                            <label>Responsabilidad Civil (Póliza)</label>
                            <input
                                type="text"
                                name="preexistencias_responsabilidad_civil"
                                value={formData.preexistencias_responsabilidad_civil || ''}
                                onChange={prvHandleChange}
                                className="input"
                                placeholder="Ej: 300.000 €"
                            />
                        </div>
                    </div>

                    <div className="form-row" style={{ marginTop: '12px' }}>
                        <div className="form-group">
                            <label>Continente Calculado (Perito)</label>
                            <input
                                type="text"
                                name="preexistencias_continente_calculado"
                                value={formData.preexistencias_continente_calculado || ''}
                                onChange={prvHandleChange}
                                className="input"
                                placeholder="Ej: 165.000 €"
                            />
                        </div>
                        <div className="form-group">
                            <label>Contenido Calculado (Perito)</label>
                            <input
                                type="text"
                                name="preexistencias_contenido_calculado"
                                value={formData.preexistencias_contenido_calculado || ''}
                                onChange={prvHandleChange}
                                className="input"
                                placeholder="Ej: 35.000 €"
                            />
                        </div>
                        <div className="form-group">
                            <label>Responsabilidad Civil Calculada (Perito)</label>
                            <input
                                type="text"
                                name="preexistencias_responsabilidad_civil_calculada"
                                value={formData.preexistencias_responsabilidad_civil_calculada || ''}
                                onChange={prvHandleChange}
                                className="input"
                                placeholder="Ej: 300.000 €"
                            />
                        </div>
                    </div>
                </section>

                {/* Observaciones */}
                <section className="form-section">
                    <h3 className="section-title">Observaciones</h3>
                    <div className="form-group full-width">
                        <textarea
                            name="observaciones"
                            value={formData.observaciones}
                            onChange={prvHandleChange}
                            className="input textarea"
                            rows={4}
                            placeholder="Observaciones iniciales o notas del encargo..."
                        />
                    </div>
                </section>
            </div>

        </form>
    );
};

export default EncargoForm;
